import React, { useEffect, useState } from 'react';
import {
  Paper,
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { initAuth, setUser } from '../redux/user.redux';
import { getAuthConfig, devLogin } from '../api/login';
import { LiquidProviderIcon } from '../assets/icons';

export default function Login() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isLogin = useAppSelector((s) => s.user.isLogin);
  const [loading, setLoading] = useState(true);
  const [oidcConfigured, setOidcConfigured] = useState(false);
  const [devEnabled, setDevEnabled] = useState(false);
  const [username, setUsername] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errMsg, setErrMsg] = useState('');

  // 检测已有会话
  useEffect(() => {
    dispatch(initAuth())
      .unwrap()
      .catch(() => {})
      .finally(() => setLoading(false));
    getAuthConfig()
      .then((r) => {
        setOidcConfigured(r.data.oidc_configured);
        setDevEnabled(r.data.dev_enabled);
      })
      .catch(() => {});
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (isLogin) navigate('/', { replace: true });
    // eslint-disable-next-line
  }, [isLogin]);

  const doOidcLogin = () => {
    window.location.href = '/api/auth/login?redirect=%2F';
  };

  const doDevLogin = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!username.trim()) {
      setErrMsg('请输入用户名');
      return;
    }
    setSubmitting(true);
    setErrMsg('');
    try {
      const r = await devLogin(username.trim());
      dispatch(setUser(r.data.user));
      navigate('/', { replace: true });
    } catch (err: any) {
      setErrMsg(err?.message || '登录失败');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'primary.dark',
      }}
    >
      <Paper
        elevation={6}
        sx={{
          width: 420,
          p: '40px 36px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          borderRadius: 3,
        }}
      >
        <LiquidProviderIcon />
        <Typography variant="h6" sx={{ mt: 2, color: 'primary.main', fontWeight: 600 }}>
          CloakChat
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5, mb: 3 }}>
          安全 · 私密的即时通讯
        </Typography>

        {oidcConfigured ? (
          <>
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={doOidcLogin}
              sx={{ py: 1.2, borderRadius: 2 }}
            >
              使用 Cube 账号登录
            </Button>
            {devEnabled && (
              <>
                <Divider sx={{ width: '100%', my: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    或使用开发账号
                  </Typography>
                </Divider>
                <Box component="form" onSubmit={doDevLogin} sx={{ width: '100%' }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="开发用户名"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    sx={{ mb: 2 }}
                  />
                  <LoadingButton
                    fullWidth
                    variant="outlined"
                    size="large"
                    loading={submitting}
                    onClick={() => doDevLogin()}
                    sx={{ borderRadius: 2 }}
                  >
                    开发登录
                  </LoadingButton>
                </Box>
              </>
            )}
          </>
        ) : (
          <Box component="form" onSubmit={doDevLogin} sx={{ width: '100%' }}>
            <TextField
              fullWidth
              label="用户名"
              placeholder="输入用户名登录"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoFocus
              sx={{ mb: 2 }}
            />
            <LoadingButton
              fullWidth
              variant="contained"
              size="large"
              loading={submitting}
              type="submit"
              sx={{ py: 1.2, borderRadius: 2 }}
            >
              登 录
            </LoadingButton>
          </Box>
        )}

        {errMsg && (
          <Alert severity="error" sx={{ width: '100%', mt: 2 }}>
            {errMsg}
          </Alert>
        )}
      </Paper>
    </Box>
  );
}
